import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const CATS = ["tshirts", "hoodies", "accessories"];
const SIZES = ["XS", "S", "M", "L", "XL"];
const EMPTY = { name: "", category: "tshirts", price: "", color: "", images: "", description: "", featured: false, stock: { XS: 10, S: 10, M: 10, L: 10, XL: 10 } };

const toForm = (p) => (p ? { ...p, images: (p.images || []).join("\n"), price: String(p.price) } : EMPTY);

export const ProductForm = ({ open, onOpenChange, product, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const sizeList = form.category === "accessories" ? ["One Size"] : ["XS", "S", "M", "L", "XL"];

  useEffect(() => {
    if (open) setForm(toForm(product));
  }, [open, product]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const input = "mt-1 w-full border border-line bg-bone px-3 py-2 text-sm outline-none focus:border-ink";

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const urls = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/admin/upload", fd);
        urls.push(data.url);
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err)}`);
      }
    }
    setForm((prev) => ({ ...prev, images: [...prev.images.split(/\n|,/).map((s) => s.trim()).filter(Boolean), ...urls].join("\n") }));
    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
    if (urls.length) toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} uploaded`);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      name: form.name, category: form.category, price: parseFloat(form.price), color: form.color,
      description: form.description, featured: form.featured, sizes: sizeList, active: true,
      images: form.images.split(/\n|,/).map((s) => s.trim()).filter(Boolean),
      stock: Object.fromEntries(sizeList.map((s) => [s, parseInt(form.stock?.[s] ?? 10, 10) || 0])),
    };
    try {
      if (product) await api.put(`/admin/products/${product.product_id}`, body);
      else await api.post("/admin/products", body);
      toast.success(product ? "Product updated" : "Product created");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-form-dialog" data-lenis-prevent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-none bg-bone">
        <DialogHeader><DialogTitle className="font-display text-3xl font-light">{product ? "Edit garment" : "New garment"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <label className="block text-xs">Name<input required value={form.name} onChange={set("name")} className={input} data-testid="pf-name" /></label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block text-xs">Category
              <select value={form.category} onChange={set("category")} className={input} data-testid="pf-category">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
            </label>
            <label className="block text-xs">Price (£)<input required type="number" min="1" step="0.01" value={form.price} onChange={set("price")} className={input} data-testid="pf-price" /></label>
            <label className="block text-xs">Colour<input value={form.color} onChange={set("color")} className={input} data-testid="pf-color" /></label>
          </div>
          <div className="rounded-none border border-line bg-white p-3">
            <p className="text-xs">Upload photos from your computer</p>
            <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={onFiles} data-testid="pf-file-input" className="mt-2 block w-full text-xs file:mr-3 file:cursor-pointer file:border-none file:bg-ink file:px-3 file:py-2 file:text-xs file:text-bone" />
            {uploading && <p data-testid="pf-uploading" className="mt-2 flex items-center gap-2 text-xs text-stone"><Loader2 className="h-3 w-3 animate-spin" />Uploading…</p>}
          </div>
          <label className="block text-xs">Image URLs (one per line)<textarea required rows={3} value={form.images} onChange={set("images")} className={input} data-testid="pf-images" /></label>
          <label className="block text-xs">Description<textarea rows={3} value={form.description} onChange={set("description")} className={input} data-testid="pf-description" /></label>
          <div>
            <p className="text-xs">Stock per size</p>
            <div className={`mt-1 grid gap-2 ${sizeList.length > 1 ? "grid-cols-5" : "grid-cols-1"}`}>
              {sizeList.map((s) => (
                <label key={s} className="text-center font-mono text-[11px]">{s}
                  <input type="number" min="0" value={form.stock?.[s] ?? 10} onChange={(e) => setForm({ ...form, stock: { ...form.stock, [s]: e.target.value } })} className={input} data-testid={`pf-stock-${s}`} />
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-3 text-sm"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="pf-featured" />Featured on homepage</label>
          <button type="submit" disabled={saving} data-testid="pf-save-button" className="w-full rounded-full bg-ink py-3 text-sm text-bone hover:bg-signal disabled:opacity-60">{saving ? "Saving…" : "Save garment"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
